import React, { useState, useEffect } from 'react';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import { Link, useParams, useNavigate } from 'react-router-dom';
// Import more Prism languages as needed

// List your post filenames, titles, and dates here
const postFiles = [
  { fileName: 'web_agent_tutorial.html', title: 'Tutorial: Building a Browser Use Agent From Scratch and with Magentic-UI', date: '2025-06-15' },
  // Add more posts here as you add them
];

function parseFrontmatter(md: string) {
  const match = /^---\n([\s\S]+?)\n---\n([\s\S]*)$/m.exec(md);
  if (!match) return { data: {}, content: md };
  const data = Object.fromEntries(
    match[1].split('\n').map(line => {
      const [key, ...rest] = line.split(':');
      return [key.trim(), rest.join(':').trim().replace(/^'|'$/g, '')];
    })
  );
  return { data, content: match[2] };
}

interface PostMeta {
  title: string;
  date: string;
  [key: string]: any;
}

interface Post {
  slug: string;
  meta: PostMeta;
  content: string; // HTML string
}

const Blog: React.FC = () => {
  const [postList, setPostList] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const { slug } = useParams();
  const navigate = useNavigate();
  const selectedPost = slug ? postList.find((p) => p.slug === slug) || null : null;

  useEffect(() => {
    async function loadPosts() {
      const loadedPosts: Post[] = await Promise.all(
        postFiles.map(async ({ fileName, title, date }) => {
          const res = await fetch(`/posts/${fileName}`);
          const text = await res.text();
          const meta = { title, date };
          const htmlContent = text;
          return {
            slug: fileName.replace(/\.html$/, ''),
            meta,
            content: htmlContent,
          };
        })
      );
      setPostList(loadedPosts);
      setLoading(false);
    }
    loadPosts();
  }, []);

  useEffect(() => {
    if (selectedPost) {
      Prism.highlightAll();
    }
  }, [selectedPost]);

  if (loading) return <div className="text-zinc-400">Loading blog posts...</div>;

  if (slug && selectedPost) {
    return (
      <article className="max-w-3xl mx-auto min-w-0">
        <button onClick={() => navigate('/blog')} className="mb-6 text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors">← Back to all posts</button>
        <div className="font-mono text-sm text-zinc-400 mb-3">{selectedPost.meta.date}</div>
        <div className="blog-post" dangerouslySetInnerHTML={{ __html: selectedPost.content }} />
      </article>
    );
  }

  return (
    <div>
      <h1 className="page-title mb-10">Blog</h1>
      <ul>
        {postList.map((post) => (
          <li key={post.slug} className="py-5 border-b border-zinc-100 last:border-0 grid grid-cols-1 sm:grid-cols-[7rem_1fr] gap-x-6 gap-y-1">
            <div className="font-mono text-sm text-zinc-400 pt-0.5 sm:order-1">{post.meta.date}</div>
            <Link
              to={`/blog/${post.slug}`}
              className="text-[1.05rem] font-semibold leading-snug !text-zinc-900 hover:!text-accent-600 hover:no-underline sm:order-2"
            >
              {post.meta.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Blog;
